import { useEffect, useMemo, useRef, useState } from "react";
import Select from "react-select";
import Header from "../../../components/Header";
import LoadingOverlay from "../../../components/LoadingOverlay";
import ReportFiltersCard, {
    ReportFilterField,
} from "../../../components/reports/ReportFiltersCard";
import Sidebar from "../../../components/Sidebar";
import { getRetentionReport } from "../../../services/analytics/getRetentionReport";
import { getManagers } from "../../../services/employees/getManagers";
import RetentionTable from "./components/RetentionTable";
import RetentionDetailsTable from "./components/RetentionDetailsTable";
import { getRetentionDetails } from "../../../services/analytics/getRetentionDetails";

const DEFAULT_ID = "00000000-0000-0000-0000-000000000000";

export default function RetentionReport() {
    const [displayRetentionDetails, setDisplayRetentionDetails] = useState(false);
    const [retentionDetails, setRetentionDetails] = useState([]);
    const [managers, setManagers] = useState([
        {
            value: DEFAULT_ID,
            label: "All",
            is_showing: true,
        },
    ]);
    const [selectedManager, setSelectedManager] = useState({
        value: DEFAULT_ID,
        label: "All",
    });
    const now = new Date();
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [filters, setFilters] = useState({
        period: "Quarterly",
        subscriptionType: "Subscription",
        year: now.getFullYear(),
        clientType: "",
        managerId: DEFAULT_ID,
        renewalGoal: 97,
    });
    const [appliedFilters, setAppliedFilters] = useState({
        period: "Quarterly",
        subscriptionType: "Subscription",
        year: now.getFullYear(),
        clientType: "",
        managerId: DEFAULT_ID,
        renewalGoal: 97,
    });
    const [dataForReport, setDataForReport] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState("");
    const resultsSectionRef = useRef(null);
    const shouldScrollToResultsRef = useRef(false);

    const last4Years = useMemo(() => {
        const y = now.getFullYear();
        return [y, y - 1, y - 2, y - 3];
    }, [now]);

    const handleChangeRenewalGoal = (e) => {
        const raw = e.target.value;

        if (raw === "") {
            setFilters((currentFilters) => ({
                ...currentFilters,
                renewalGoal: "",
            }));
            return;
        }

        const digits = raw.replace(/\D/g, "");

        if (digits === "") {
            setFilters((currentFilters) => ({
                ...currentFilters,
                renewalGoal: "",
            }));
            return;
        }

        let val = parseInt(digits, 10);

        if (val < 0) val = 0;
        if (val > 100) val = 100;

        setFilters((currentFilters) => ({
            ...currentFilters,
            renewalGoal: val,
        }));
    };

    const handleBlurRenewalGoal = () => {
        if (filters.renewalGoal === "" || filters.renewalGoal == null) {
            setFilters((currentFilters) => ({
                ...currentFilters,
                renewalGoal: 0,
            }));
            return;
        }

        let val = parseInt(String(filters.renewalGoal), 10);
        if (Number.isNaN(val)) val = 0;
        if (val < 0) val = 0;
        if (val > 100) val = 100;

        setFilters((currentFilters) => ({
            ...currentFilters,
            renewalGoal: val,
        }));
    };

    const onChangePeriod = (period) => {
        setFilters((currentFilters) => ({
            ...currentFilters,
            period,
            renewalGoal: period === "Quarterly" ? 97 : 88,
        }));
    };

    const fetchRetentionData = async () => {
        setIsLoading(true);
        setError("");

        const payload =
            appliedFilters.period === "Annually"
                ? {
                      period: 0,
                      subscriptionType:
                          appliedFilters.subscriptionType == "Subscription"
                              ? 1
                              : 2,
                      managerId: appliedFilters.managerId,
                      year: appliedFilters.year,
                      clientType:
                          appliedFilters.clientType == ""
                              ? null
                              : appliedFilters.clientType,
                  }
                : {
                      period: 1,
                      subscriptionType:
                          appliedFilters.subscriptionType == "Subscription"
                              ? 1
                              : 2,
                      managerId: appliedFilters.managerId,
                      year: appliedFilters.year,
                      clientType:
                          appliedFilters.clientType == ""
                              ? null
                              : appliedFilters.clientType,
                  };

        const { success, data } = await getRetentionReport(payload);
        console.log("data", data.retentionReport);

        if (!success) {
            if (appliedFilters.period === "Quarterly") {
                setDataForReport([
                    {
                        period: `Q1 ${appliedFilters.year}`,
                        retained: 120,
                        churned: 5,
                        total: 125,
                        percentRenewed: 96.0,
                    },
                    {
                        period: `Q2 ${appliedFilters.year}`,
                        retained: 140,
                        churned: 3,
                        total: 143,
                        percentRenewed: 97.9,
                    },
                    {
                        period: `Q3 ${appliedFilters.year}`,
                        retained: 118,
                        churned: 9,
                        total: 127,
                        percentRenewed: 92.9,
                    },
                    {
                        period: `Q4 ${appliedFilters.year}`,
                        retained: 130,
                        churned: 4,
                        total: 134,
                        percentRenewed: 97.0,
                    },
                ]);
            } else {
                const fake = last4Years.map((y) => {
                    const retained = Math.floor(400 + Math.random() * 80);
                    const churned = Math.floor(20 + Math.random() * 20);
                    const total = retained + churned;
                    const percentRenewed =
                        total > 0 ? +((retained / total) * 100).toFixed(1) : 0;
                    return {
                        period: `${y}`,
                        retained,
                        churned,
                        total,
                        percentRenewed,
                    };
                });
                setDataForReport(fake);
            }
        } else {
            if (appliedFilters.period === "Annually") {
                const normalized = (data.retentionReport || []).map((r) => {
                    const retained = r.retained ?? 0;
                    const churned = r.churned ?? 0;
                    const total = retained + churned;
                    const percentRenewed =
                        total > 0 ? +((retained / total) * 100).toFixed(1) : 0;
                    return {
                        period: `${r.period}`,
                        retained,
                        churned,
                        total,
                        percentRenewed,
                    };
                });
                setDataForReport(normalized);
            } else {
                setDataForReport(data.retentionReport || []);
            }
        }

        setIsLoading(false);
    };

    useEffect(() => {
        const fetchManagers = async () => {
            const response = await getManagers();
            console.log("Managers response:", response);
            if (response.success) {
                const managersOptions = [
                    { value: DEFAULT_ID, label: "All", is_showing: true },
                    ...(response.data.managers || []).map((e) => ({
                        value: e.employeeId,
                        label: `${e.firstName} ${e.lastName}`,
                        is_showing: false,
                    })),
                ];

                setManagers(managersOptions);

                // Set the selected manager
                setSelectedManager((prev) => {
                    if (!prev || !prev.value) {
                        return managersOptions[0];
                    }
                    const foundManager = managersOptions.find(
                        (manager) => manager.value === prev.value,
                    );
                    return foundManager || managersOptions[0];
                });
            }
        };
        fetchManagers();
    }, []);

    useEffect(() => {
        setDisplayRetentionDetails(false);
        fetchRetentionData();
    }, [appliedFilters]);

    useEffect(() => {
        if (isLoading || !shouldScrollToResultsRef.current) {
            return;
        }

        if (resultsSectionRef.current) {
            resultsSectionRef.current.scrollIntoView({
                behavior: "smooth",
                block: "start",
            });
        }

        shouldScrollToResultsRef.current = false;
    }, [dataForReport, isLoading]);

    const subtitle = useMemo(() => {
        const managerName =
            managers.find((manager) => manager.value === appliedFilters.managerId)
                ?.label || "All Managers";
        if (appliedFilters.period === "Annually") {
            return `Manager: ${managerName} · Period: Annually (last 4 years: ${last4Years.join(
                ", ",
            )}) · Type: ${appliedFilters.subscriptionType}`;
        }
        return `Manager: ${managerName} · Period: Quarterly · Year: ${appliedFilters.year} · Type: ${appliedFilters.subscriptionType}`;
    }, [appliedFilters, last4Years, managers]);

    const onChangeSelectedManager = (e) => {
        setSelectedManager(e);
        setFilters((currentFilters) => ({
            ...currentFilters,
            managerId: e?.value || DEFAULT_ID,
        }));
    };

    const handleApplyFilters = () => {
        shouldScrollToResultsRef.current = true;
        setDisplayRetentionDetails(false);
        setRetentionDetails([]);
        setAppliedFilters({ ...filters });
    };

    const displayDetails = ({ quarterName, metric }) => {
        const payload = {
            period: appliedFilters.period === "Annually" ? 0 : 1,
            subscriptionType:
                appliedFilters.subscriptionType === "Subscription" ? 1 : 2,
            managerId: appliedFilters.managerId,
            year: quarterName.split(" ")[1],
            metric: metric === "retained" ? 0 : metric === "churned" ? 1 : 2,
            clientType:
                appliedFilters.clientType === ""
                    ? null
                    : appliedFilters.clientType,
        };

        if (payload.period === 1) {
            switch (quarterName.split(" ")[0]) {
                case "Q1":
                    payload.quarterNumber = 1;
                    break;
                case "Q2":
                    payload.quarterNumber = 2;
                    break;
                case "Q3":
                    payload.quarterNumber = 3;
                    break;
                default:
                    payload.quarterNumber = 4;
            }
        }

        const fetchRetentionDetails = async () => {
            console.log("Fetching retention details with payload:", payload);
            const res = await getRetentionDetails(payload);
            if (!res.success) {
                console.log("Failed to fetch retention details");
                return;
            }
            setRetentionDetails(res.data.items || []);
            setDisplayRetentionDetails(true);
        };
        fetchRetentionDetails();
    };

    return (
        <div className="flex h-screen overflow-hidden">
            <LoadingOverlay isLoading={isLoading} />
            <Sidebar sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />
            <div className="relative flex flex-col flex-1 overflow-x-hidden overflow-y-auto">
                <Header sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />
                <main>
                    <div className="w-full px-4 py-8 mx-auto sm:px-6 lg:px-8 max-w-9xl">
                        <div className="flex flex-col items-start max-w-3xl">
                            <h1 className="text-2xl font-bold md:text-3xl text-slate-800">
                                Retention
                            </h1>
                            <p className="mt-1 text-sm text-slate-500">{subtitle}</p>
                        </div>

                        <ReportFiltersCard
                            actions={
                                <button
                                    type="button"
                                    onClick={handleApplyFilters}
                                    disabled={isLoading}
                                    className="inline-flex items-center px-4 py-2 text-sm font-medium text-white transition bg-teal-500 rounded-lg shadow-sm hover:bg-teal-600 disabled:cursor-not-allowed disabled:opacity-60"
                                >
                                    Apply Filters
                                </button>
                            }
                            fieldsClassName="grid grid-cols-1 gap-4 xl:grid-cols-7"
                        >
                            <ReportFilterField label="Period">
                                <select
                                    value={filters.period}
                                    onChange={(e) => onChangePeriod(e.target.value)}
                                    disabled={isLoading}
                                    className="w-full border border-gray-300 rounded-md form-input disabled:bg-slate-100 disabled:text-slate-500"
                                >
                                    <option value="Quarterly">Quarterly</option>
                                    <option value="Annually">Annually</option>
                                </select>
                            </ReportFilterField>

                            <ReportFilterField label="Subscription Type">
                                <select
                                    value={filters.subscriptionType}
                                    onChange={(e) =>
                                        setFilters((currentFilters) => ({
                                            ...currentFilters,
                                            subscriptionType: e.target.value,
                                        }))
                                    }
                                    disabled={isLoading}
                                    className="w-full border border-gray-300 rounded-md form-input disabled:bg-slate-100 disabled:text-slate-500"
                                >
                                    <option value="Subscription">Subscription</option>
                                    <option value="Non-subscription">
                                        Non-subscription
                                    </option>
                                </select>
                            </ReportFilterField>

                            <ReportFilterField label="Client Type">
                                <select
                                    value={filters.clientType}
                                    onChange={(e) =>
                                        setFilters((currentFilters) => ({
                                            ...currentFilters,
                                            clientType:
                                                e.target.value === ""
                                                    ? ""
                                                    : Number(e.target.value),
                                        }))
                                    }
                                    disabled={isLoading}
                                    className="w-full border border-gray-300 rounded-md form-input disabled:bg-slate-100 disabled:text-slate-500"
                                >
                                    <option value="">All</option>
                                    <option value={1}>Individual</option>
                                    <option value={2}>Business</option>
                                </select>
                            </ReportFilterField>

                            <ReportFilterField label="Year">
                                <select
                                    value={filters.year}
                                    onChange={(e) =>
                                        setFilters((currentFilters) => ({
                                            ...currentFilters,
                                            year: Number(e.target.value),
                                        }))
                                    }
                                    disabled={
                                        isLoading || filters.period === "Annually"
                                    }
                                    className="w-full border border-gray-300 rounded-md form-input disabled:bg-slate-100 disabled:text-slate-500"
                                >
                                    {Array.from(
                                        { length: 7 },
                                        (_, i) => now.getFullYear() - 3 + i,
                                    ).map((optionYear) => (
                                        <option key={optionYear} value={optionYear}>
                                            {optionYear}
                                        </option>
                                    ))}
                                </select>
                            </ReportFilterField>

                            <ReportFilterField label="Manager">
                                <Select
                                    onChange={(e) => onChangeSelectedManager(e)}
                                    classNamePrefix="select"
                                    isSearchable={true}
                                    value={selectedManager}
                                    options={managers}
                                    name="manager"
                                    isDisabled={isLoading}
                                />
                            </ReportFilterField>

                            <ReportFilterField label="Renewal Goal">
                                <div className="flex items-center gap-2">
                                    <input
                                        type="number"
                                        className="w-full border border-gray-300 rounded-md form-input"
                                        value={
                                            filters.renewalGoal === ""
                                                ? ""
                                                : filters.renewalGoal
                                        }
                                        step={1}
                                        min={0}
                                        max={100}
                                        inputMode="numeric"
                                        pattern="\d*"
                                        onChange={handleChangeRenewalGoal}
                                        onBlur={handleBlurRenewalGoal}
                                        disabled={isLoading}
                                        onKeyDown={(e) => {
                                            if (
                                                [".", ",", "e", "E", "+", "-"].includes(
                                                    e.key,
                                                )
                                            ) {
                                                e.preventDefault();
                                            }
                                        }}
                                    />
                                    <span className="text-sm font-medium text-slate-600">
                                        %
                                    </span>
                                </div>
                            </ReportFilterField>
                        </ReportFiltersCard>

                        {error && (
                            <div className="p-3 mt-6 text-sm text-red-700 border border-red-200 rounded-lg shadow-sm bg-red-50">
                                {error}
                            </div>
                        )}

                        <div ref={resultsSectionRef} className="mt-6">
                            <RetentionTable
                                year={
                                    appliedFilters.period === "Annually"
                                        ? last4Years[0]
                                        : appliedFilters.year
                                }
                                title={`Client Retention – ${
                                    appliedFilters.period === "Annually"
                                        ? "Last 4 Years"
                                        : appliedFilters.year
                                }`}
                                subtitle={subtitle}
                                data={dataForReport}
                                renewalGoal={appliedFilters.renewalGoal}
                                onDisplayDetails={displayDetails}
                            />
                        </div>

                        {displayRetentionDetails && (
                            <>
                                <h2 className="mt-10 text-xl font-bold text-slate-800">
                                    Retention Details
                                </h2>
                                <p className="mt-1 text-sm text-slate-500">
                                    Detailed information about client retention.
                                </p>
                                <div className="mt-4">
                                    <RetentionDetailsTable data={retentionDetails} />
                                </div>
                            </>
                        )}
                    </div>
                </main>
            </div>
        </div>
    );
}
